import React, { useEffect, useState } from 'react'
import { hapticTick } from '../../../../../../_utils/haptics'
import { isShared, reviewWhen } from '../../data/reviews'
import ReviewsPanel, { Stars, useSalonReviews } from '../ReviewsPanel'
import { ChevronRight } from '../icons'

const lower = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : '')

const FEW = 3

// A review post's words come from one of the salon's own reviews: pick one
// and it fills in their words, first name, stars and service, word for word.
// The newest kind ones show here and every review is in a panel with search
// and star filters (some salons have hundreds). Or type one in (from Google
// or a message).
const ReviewPicker = ({ post, onPick, onTypeOwn }) => {
  const reviews = useSalonReviews()
  const current = /^review:/.test(post.source || '') ? String(post.source).slice(7) : null
  const [open, setOpen] = useState(!current)
  const [all, setAll] = useState(false)
  // Picked: the list folds away to show what's in the post
  useEffect(() => { if (current) setOpen(false) }, [current])
  const c = post.content || {}
  const chosen = current ? reviews.list.find(r => String(r.id) === current) : null

  if (current && !open) {
    return (
      <div className="sx-rp-current">
        <div className="sx-rp-current-text">
          <Stars n={Number(c.rating) || 5} />
          <span className="sx-review-who">{c.name}{c.service ? `, ${c.service}` : ''}</span>
          <span className="sx-rp-note">From your reviews, word for word{chosen && chosen.at ? `. Left ${lower(reviewWhen(chosen.at))}` : ''}.</span>
          {onTypeOwn && <button type="button" className="sx-link sx-rp-own" onClick={() => { hapticTick(); onTypeOwn() }}>Type one in instead</button>}
        </div>
        <button type="button" className="sx-btn" onClick={() => { hapticTick(); setOpen(true) }}>Choose another</button>
      </div>
    )
  }

  const waiting = !reviews.at && !reviews.failed
  const kind = reviews.list.filter(r => r.rating >= 4)
  const pool = kind.length ? kind : reviews.list
  // The one in the post stays in view while choosing another
  const few = (chosen ? [chosen, ...pool.filter(r => r !== chosen)] : pool).slice(0, FEW)
  const more = reviews.hasMore || reviews.list.length > few.length
  const total = reviews.total && reviews.total > few.length ? reviews.total : null

  return (
    <section className="sx-rp" aria-label="Pick a review">
      <div className="sx-rp-head">
        <h3 className="sx-group-title">Pick one of your reviews</h3>
        <p className="sx-rp-line">{current ? 'Tap another to use it instead.' : 'Tap one and it fills in their words, first name, stars and service. Or type one in below, from Google or a message.'}</p>
      </div>
      {waiting && (
        <div className="sx-rp-list" aria-hidden>
          {[0, 1, 2].map(i => <span key={i} className="sx-rp-row is-loading"><span className="sx-line" /><span className="sx-line is-short" /></span>)}
        </div>
      )}
      {!waiting && !few.length && !more && (
        <p className="sx-empty">{reviews.failed ? 'Your reviews couldn’t be loaded just now. You can type one in below instead.' : 'No reviews yet. When clients leave one, it shows here. You can type one in below, from Google or a message.'}</p>
      )}
      {few.length > 0 && (
        <div className="sx-rp-list">
          {few.map((r) => {
            const on = String(r.id) === current
            return (
              <button key={r.id} type="button" className={`sx-rp-row${on ? ' is-on' : ''}`} aria-pressed={on} onClick={(e) => { if (on) setOpen(false); else onPick(r, e.currentTarget) }}>
                <span className="sx-rp-row-top">
                  <Stars n={r.rating} />
                  <span className="sx-review-when">{reviewWhen(r.at)}{r.google ? ' · Google' : ''}</span>
                </span>
                <span className="sx-rp-text">“{r.text}”</span>
                <span className="sx-rp-row-foot">
                  <span className="sx-review-who">{r.firstName}{r.service ? `, ${r.service}` : ''}</span>
                  {on ? <span className="sx-rp-tag is-on">In this post</span> : isShared(r) ? <span className="sx-rp-tag">Shared before</span> : null}
                </span>
              </button>
            )
          })}
        </div>
      )}
      {more && (
        <button type="button" className="sx-btn sx-rp-all" onClick={() => { hapticTick(); setAll(true) }}>
          <span>{total ? `See all ${total.toLocaleString('en-GB')} reviews` : 'See all your reviews'}</span>
          <ChevronRight size={16} />
        </button>
      )}
      {current && <button type="button" className="sx-link sx-rp-keep" onClick={() => { hapticTick(); setOpen(false) }}>Keep {c.name ? `${c.name}’s` : 'this'} review</button>}
      {all && (
        <ReviewsPanel
          mode="pick"
          currentId={current}
          onUse={(r) => { setAll(false); if (String(r.id) === current) setOpen(false); else onPick(r, null) }}
          onClose={() => setAll(false)}
        />
      )}
    </section>
  )
}

export default ReviewPicker
